<!--
  FileAttachmentButton Component
  
  A reusable paperclip button that opens a dialog to view file attachments.
  
  Usage:
  <FileAttachmentButton 
    :attachments="formAttachments" 
    button-color="primary"
    button-title="View files"
  />
  
  Props:
  - attachments: Array of FormAttachment objects
  - buttonColor: Color of the button (default: "brandCyan")
  - buttonSize: Size of the button (default: "default")
  - icon: Icon to display (default: "mdi-paperclip")
  - buttonTitle: Tooltip text (default: "View attached files")
-->
<template>
  <div @click.stop>
    <!-- If there are no attachments, don't show the button -->
    <div v-if="!attachments || attachments.length === 0">
      <v-btn
        icon
        disabled
        :color="buttonColor"
        :size="buttonSize"
        :title="buttonTitle"
        :loading="loading"
      >
        <v-icon>{{ icon }}</v-icon>
      </v-btn>
    </div>
    <div v-if="attachments && attachments.length > 0">
      <template v-if="attachments.length === 1">
        <v-btn
          icon
          @click="handleDownloadFile(attachments[0])"
          :color="buttonColor"
          :size="buttonSize"
          :title="buttonTitle"
          :loading="loading"
        >
          <v-icon>{{ icon }}</v-icon>
        </v-btn>
      </template>
      <template v-else>
        <v-badge :content="attachments.length" :color="badgeColor" overlap>
          <v-btn
            icon
            @click="openDialog"
            :color="buttonColor"
            :size="buttonSize"
            :title="buttonTitle"
            :loading="loading"
          >
            <v-icon>{{ icon }}</v-icon>
          </v-btn>
        </v-badge>
      </template>

      <UploadedFilesDialog
        :attachments-item="attachments"
        :has-additional-claimants="hasAdditionalClaimants"
        :show="showDialog"
        @input="showDialog = $event"
      />
    </div>
  </div>
</template>

<script>
// Components
import UploadedFilesDialog from "@/components/forms/UploadedFilesDialog.vue";

// Mixins
import fileDownloadMixin from "@/mixins/forms/formFileDownloadMixin";

export default {
  name: "FileAttachmentButton",
  mixins: [fileDownloadMixin],
  components: {
    UploadedFilesDialog,
  },
  props: {
    attachments: {
      type: Array,
      default: () => [],
    },
    hasAdditionalClaimants: {
      type: Boolean,
      default: false,
    },
    buttonColor: {
      type: String,
      default: "brandPrimary",
    },
    buttonSize: {
      type: String,
      default: "default",
    },
    icon: {
      type: String,
      default: "mdi-paperclip",
    },
    buttonTitle: {
      type: String,
      default: "View attached files",
    },
    badgeColor: {
      type: String,
      default: "blue",
    },
  },
  data() {
    return {
      showDialog: false,
      loading: false,
    };
  },
  methods: {
    openDialog() {
      if (this.attachments && this.attachments.length > 0) {
        this.showDialog = true;
      }
    },
    async handleDownloadFile(attachment) {
      this.loading = true;
      try {
        await this.downloadFile(attachment);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Add any custom styling if needed */
</style>
